import { useCallback, createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSiteContext } from '../index';
import defaultSnowplowContextData from './utils/defaultSnowplowContextData';
import { snowplowContextIndexes } from './constants/snowplowContextIndexes';
import { jsx as _jsx } from "react/jsx-runtime";
const SnowplowTrackingContext = /*#__PURE__*/createContext();
const useSnowplowContextData = () => {
  var _window$spa_variables, _window;
  const alreadyStoredInSession = sessionStorage.getItem('snowplowContextData') || null;
  const snowplowContextData = JSON.parse(alreadyStoredInSession) || defaultSnowplowContextData;
  const {
    entryType,
    language,
    market,
    partner,
    siteCurrency,
    siteName
  } = useSiteContext();
  const {
    SITE,
    REFERRER
  } = snowplowContextIndexes;

  // Populate SITE and REFERRER data since they are always available
  snowplowContextData[SITE].data.scenarios = (_window$spa_variables = (_window = window) === null || _window === void 0 || (_window = _window.spa_variables) === null || _window === void 0 ? void 0 : _window.scenarios) !== null && _window$spa_variables !== void 0 ? _window$spa_variables : [];
  snowplowContextData[SITE].data.siteCountry = market === null || market === void 0 ? void 0 : market.code;
  snowplowContextData[SITE].data.siteCurrency = siteCurrency === null || siteCurrency === void 0 ? void 0 : siteCurrency.code;
  snowplowContextData[SITE].data.siteLanguage = language === null || language === void 0 ? void 0 : language.code.toUpperCase();
  snowplowContextData[SITE].data.siteName = siteName;
  snowplowContextData[REFERRER].data.entryType = entryType;
  snowplowContextData[REFERRER].data.partner = partner !== '' ? partner : null;
  if (!alreadyStoredInSession) {
    sessionStorage.setItem('snowplowContextData', JSON.stringify(snowplowContextData));
  }
  return snowplowContextData;
};
const SnowplowTrackingProvider = _ref => {
  let {
    isEnabled,
    ...rest
  } = _ref;
  const snowplowContextData = useSnowplowContextData();
  return /*#__PURE__*/_jsx(SnowplowTrackingContext.Provider, {
    value: {
      isEnabled,
      snowplowContextData
    },
    ...rest
  });
};
const sendEvent = function (category, action, label, property, value, context) {
  if (property === void 0) {
    property = '';
  }
  if (value === void 0) {
    value = '';
  }
  if (window.snowplow) {
    window.snowplow('trackStructEvent', {
      category,
      action,
      label,
      property,
      value,
      context
    });
  }
};
const updateContextData = (_ref2, defaultContextData) => {
  let {
    flightData: newFlightData,
    passengerData: newPassengerData,
    productData: newProductData,
    referrerData: newReferrerData,
    siteData: newSiteData,
    userData: newUserData
  } = _ref2;
  const {
    FLIGHT,
    PASSENGER,
    PRODUCT,
    REFERRER,
    SITE,
    USER
  } = snowplowContextIndexes;
  const newContextData = [{
    schema: defaultContextData[FLIGHT].schema,
    data: {
      ...defaultContextData[FLIGHT].data,
      ...newFlightData
    }
  }, {
    schema: defaultContextData[PASSENGER].schema,
    data: {
      ...defaultContextData[PASSENGER].data,
      ...newPassengerData
    }
  }, {
    schema: defaultContextData[PRODUCT].schema,
    data: {
      ...defaultContextData[PRODUCT].data,
      ...newProductData
    }
  }, {
    schema: defaultContextData[REFERRER].schema,
    data: {
      ...defaultContextData[REFERRER].data,
      ...newReferrerData
    }
  }, {
    schema: defaultContextData[SITE].schema,
    data: {
      ...defaultContextData[SITE].data,
      ...newSiteData
    }
  }, {
    schema: defaultContextData[USER].schema,
    data: {
      ...defaultContextData[USER].data,
      ...newUserData
    }
  }];
  sessionStorage.setItem('snowplowContextData', JSON.stringify(newContextData));
  return newContextData;
};
const useSnowplowTracking = () => {
  const context = useContext(SnowplowTrackingContext);
  if (!context) {
    // eslint-disable-next-line no-console
    console.warn('useSnowplowTracking must be used within a SnowplowTrackingProvider');
  }
  const isSnowplowTrackingEnabled = context ? context.isEnabled : false;
  const snowplowContextData = useMemo(() => context ? context.snowplowContextData : {}, [context]);
  const sendTrackEvent = useCallback(_ref3 => {
    let {
      category,
      action,
      label,
      property,
      value,
      contextData = JSON.parse(sessionStorage.getItem('snowplowContextData')) || snowplowContextData
    } = _ref3;
    if (context) {
      sendEvent(category, action, label, property, value, contextData);
    }
  }, [context, snowplowContextData]);
  const updateSnowplowContextData = useCallback(newContextData => {
    if (context) {
      return updateContextData(newContextData, snowplowContextData);
    }
    return null;
  }, [context, snowplowContextData]);
  return {
    isSnowplowTrackingEnabled,
    sendTrackEvent,
    updateSnowplowContextData
  };
};
SnowplowTrackingProvider.propTypes = {
  isEnabled: PropTypes.bool
};
export { SnowplowTrackingProvider, useSnowplowTracking };
//# sourceMappingURL=SnowplowTrackingContext.js.map